import React from 'react'
import { motion, useReducedMotion, Variants } from 'framer-motion'
import EditableText from '@/components/editable/EditableText'
import { useSiteEdit } from '@/contexts/SiteEditContext'

const Manifeste: React.FC = () => {
  const prefersReduced = useReducedMotion()
  const { get, canEdit, editMode } = useSiteEdit()

  const container: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: prefersReduced ? 0 : 0.04, delayChildren: 0.1 },
    },
  }

  const word: Variants = {
    hidden: { opacity: 0, y: 30, filter: 'blur(8px)' },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    },
  }

  const renderWords = (text: string, className?: string) =>
    text.split(' ').map((w, i) => (
      <span key={`${className ?? ''}-${i}`} className="inline-block overflow-hidden align-top">
        <motion.span variants={word} className={`inline-block ${className ?? ''}`}>
          {w}
          {i < text.split(' ').length - 1 ? '\u00A0' : ''}
        </motion.span>
      </span>
    ))

  return (
    <section className="relative bg-[#0E1B2C] py-32 lg:py-48 overflow-hidden">
      {/* Animated radial glow */}
      <motion.div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-[1100px] h-[600px] pointer-events-none blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(184,194,107,0.16), transparent 70%)' }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-[#C9E26A]/40"
            style={{
              left: `${(i * 13 + 10) % 90}%`,
              top: `${(i * 17 + 20) % 80}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.7, 0.2],
            }}
            transition={{
              duration: 6 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.4,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-6">
            <EditableText as="span" contentKey="manifeste.section.kicker" section="Manifeste" label="Titre de section">MANIFESTE</EditableText>
          </div>
          <motion.div
            className="h-px bg-gradient-to-r from-transparent via-[#C8A24A] to-transparent mx-auto mb-12"
            initial={{ width: 0 }}
            whileInView={{ width: 128 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>

        {canEdit && editMode ? (
          <EditableText
            as="p"
            multiline
            contentKey="manifeste.quote"
            section="Manifeste"
            label="Manifeste — citation principale"
            className="font-serif-display text-[28px] sm:text-[40px] lg:text-[56px] leading-[1.18] text-[#E8DCC4] text-balance"
          >
            {get('manifeste.quote', 'Le Centre ne doit pas fonctionner comme un "business vide". L\'idée est de créer de la valeur réelle, une expérience réelle, un impact réel, une utilité réelle.')}
          </EditableText>
        ) : (
          <motion.p
            className="font-serif-display text-[28px] sm:text-[40px] lg:text-[56px] leading-[1.18] text-[#E8DCC4] text-balance"
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {renderWords(get('manifeste.quote', 'Le Centre ne doit pas fonctionner comme un "business vide". L\'idée est de créer de la valeur réelle, une expérience réelle, un impact réel, une utilité réelle.'))}
          </motion.p>
        )}

        <motion.div
          className="mt-16 flex flex-col items-center gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          <motion.div
            className="w-px bg-gradient-to-b from-transparent to-[#C8A24A]"
            initial={{ height: 0 }}
            whileInView={{ height: 64 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl"
          >
            <EditableText
              as="p"
              multiline
              contentKey="manifeste.paragraph1"
              section="Manifeste"
              label="Manifeste — paragraphe 1"
              className="text-[15px] leading-[1.8] text-[#C9BFA8]"
            >
              Construire quelque chose qui dépasse simplement un lieu physique. Créer une dynamique capable de continuer à grandir, transmettre et évoluer dans le temps.
            </EditableText>
          </motion.div>
          <motion.p
            className="max-w-2xl text-[15px] leading-[1.8] text-[#E8DCC4] mt-2"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <EditableText
              as="span"
              multiline
              contentKey="manifeste.paragraph2"
              section="Manifeste"
              label="Manifeste — paragraphe 2"
            >
              Le Centre privilégie l'expérimentation réelle, les compétences concrètes et la création de valeur durable plutôt qu'une approche théorique déconnectée du terrain.
            </EditableText>
          </motion.p>
        </motion.div>
      </div>
    </section>
  )
}

export default Manifeste
